import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertModalCheckIcon } from "../../../../assets";
import { AppLayout } from "../../../../containers/layout/app";
import { CustomerApprovalActionWrapper } from "./styled";
import { AppContext } from "../../../../context/appContext";
import { retrieveCustomerService } from "../../../../utils/api/customerservice/customerlist/retrieveCustomer";
import Cookies from "universal-cookie";
import { Box, Card, CardContent, CircularProgress, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { retrieveSavingsAccountByCustomerIdService } from "../../../../utils/outbound/savingsaccount/retrieveSavingsAccountByCustomerId";
import { BaseButton } from "../../../../components/button/styled";
import { actOnCustomerApprovalRequestService } from "../../../../utils/api/approval/customerapproval/actOnCustomerApprovalRequest";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { BaseImageModal } from "../../../../components/modal/image";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveUserProfileService } from "../../../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveInstitutionByIdService } from "../../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCountryByIdService } from "../../../../utils/outbound/country/countryById";
import { retrieveAllAccountTiersService } from "../../../../utils/api/configuration/tiermanagement/retrieveAllAccountTiers";

export const CustomerApprovalAction = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { id = "" } = useParams();
    const { setActiveNavItem } = useContext(AppContext);

    const [customer, setCustomer] = useState({} as Record<string, any>);
    const [isApproveLoading, setIsApproveLoading] = useState(false);
    const [isDeclineLoading, setIsDeclineLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [imageConfig, setImageConfig] = useState<Record<string, any>>({});
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        tier: " ",
    })

    useEffect(() => {
        setActiveNavItem("Approval");
    }, [setActiveNavItem]);

    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            try {
                const data = await retrieveCustomerService(TOKEN, id);
                const savingsAccount = await retrieveSavingsAccountByCustomerIdService(TOKEN, data.id);
                setFormDetails({
                    tier: savingsAccount?.tier || " ",
                })
                setCustomer({
                    ...data,
                    accountNumber: savingsAccount?.accountNumber || null,
                });
            } catch (err) {
                console.error("Failed to fetch customer:", err);
            }
        };
        fetchData();
    }, [TOKEN, id]);

    const { data: tiers } = useQuery({
        queryKey: ['tiers', TOKEN],
        queryFn: async () => {
            const user = await retrieveUserProfileService(TOKEN);
            const institution = await retrieveInstitutionByIdService(user?._id, user?.institution);
            const country = await retrieveCountryByIdService(user?._id, institution.country);
            const response = await retrieveAllAccountTiersService(TOKEN);
            const transformedResponse = response?.data?.filter((tier: Record<string, any>) => tier.country === country?.id);
            return transformedResponse;
        },
        enabled: !!TOKEN,
    });

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleImageModalClose = () => {
        setIsImageModalOpen(false);
        return setImageConfig({
            src: null,
            altText: null,
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageModalOpen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, imageType: string) => {
        e.preventDefault();
        e.stopPropagation();
        setIsImageModalOpen(true);
        switch (imageType) {
            case "passportPhoto":
                setImageConfig({
                    src: customer?.passport || null,
                    altText: imageType
                });
                break;
            case "signature":
                setImageConfig({
                    src: customer?.signature || null,
                    altText: imageType
                });
                break;
            case "validID":
                setImageConfig({
                    src: customer?.validId || null,
                    altText: imageType
                });
                break;
            default:
                setImageConfig({
                    src: null,
                    altText: null
                });
        };
    };

    const handleCustomerApprovalRequestAction = async (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        action: "approve" | "decline"
    ) => {
        e.preventDefault();
        e.stopPropagation();
        setError(null);
        if (!customer?.institution) return;
        const setLoading = (isLoading: boolean) => {
            setIsApproveLoading(action === "approve" ? isLoading : false);
            setIsDeclineLoading(action === "decline" ? isLoading : false);
        };
        setLoading(true);
        try {
            const response = await actOnCustomerApprovalRequestService(TOKEN, customer.institution, id, { action, tier: formDetails.tier });
            if (response.status === "success") {
                setIsAlertModalOpen(true);
            } else {
                setError(`Failed to ${action} customer account. Please check credentials and try again.`);
            }
        } catch (error: any) {
            setError(`Customer ${action} action failed. ${error.message}`);
            console.error(`Customer ${action} failed:`, error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppLayout
            pageId={"Customer-Approval"}
            pageTitle={"Customer Approval"}
        >
            <CustomerApprovalActionWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Approval Request Handled Successfully"
                    message="Your information has been successfully added to the system."
                    callToAction="Back to Approval Table"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}

                {/* image modal below */}
                <BaseImageModal
                    open={isImageModalOpen}
                    handleClose={handleImageModalClose}
                    src={imageConfig.src}
                    altText={imageConfig.altText}
                />
                {/* image modal above */}
                <Stack
                    direction={{ laptop: "row" }}
                    gap={"var(--flex-gap)"}
                >
                    <Card
                        sx={{
                            borderRadius: "1rem", border: "1px solid var(--border-diluted-violet-color)", flex: 1
                        }}
                    >
                        <CardContent
                            className="account-number card-content"
                        >
                            <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={"calc(var(--flex-gap)/4)"}
                            >
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                >
                                    Account Number:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={500}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                    whiteSpace={"normal"}
                                >
                                    {customer?.accountNumber || "Not Available"}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                    <Card
                        sx={{
                            borderRadius: "1rem", border: "1px solid var(--border-diluted-violet-color)", flex: 1
                        }}
                    >
                        <CardContent
                            className="account-tier card-content"
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Account Tier:
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="tier"
                                    sx={{ flex: 1 }}
                                    fontweight={400}
                                    value={formDetails?.tier}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Tier
                                    </BaseOption>
                                    {/* just a tiny tweak here to prevent users from having to re-chose from dropdown as customer information
                                    could cause a re-render */}
                                    {customer?.accountNumber && tiers?.map((tier: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={tier.id}
                                            >
                                                {tier.name}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </CardContent>
                    </Card>
                </Stack>
                <Stack
                    direction={{ laptop: "row" }}
                    gap={"var(--flex-gap)"}
                >
                    <Card
                        sx={{
                            borderRadius: "1rem", border: "1px solid var(--border-diluted-violet-color)", flex: 1
                        }}
                    >
                        <CardContent
                            className="profile-information card-content"
                        >
                            <Typography
                                variant="h3"
                                fontFamily={"Poppins"}
                                fontWeight={700}
                                fontSize={20}
                                lineHeight={"normal"}
                                color="var(--dark-color)"
                                marginBlockEnd={"var(--basic-margin)"}
                            >
                                Profile Information
                            </Typography>
                            <Stack
                                direction={"row"}
                                gap={"calc(var(--flex-gap)/4)"}
                                marginBlockEnd={"calc(var(--basic-margin)/2)"}
                            >
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                >
                                    Full Name:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={500}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                    whiteSpace={"normal"}
                                >
                                    {`${customer?.firstName || ""} ${customer?.lastName || ""}`}
                                </Typography>
                            </Stack>
                            <Stack
                                direction={"row"}
                                gap={"calc(var(--flex-gap)/4)"}
                                marginBlockEnd={"calc(var(--basic-margin)/2)"}
                            >
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                >
                                    Phone Number:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={500}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                    whiteSpace={"normal"}
                                >
                                    {customer?.phone}
                                </Typography>
                            </Stack>
                            <Stack
                                direction={"row"}
                                gap={"calc(var(--flex-gap)/4)"}
                                marginBlockEnd={"calc(var(--basic-margin)/2)"}
                            >
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                >
                                    Email Address:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={500}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                    whiteSpace={"normal"}
                                >
                                    {customer?.email}
                                </Typography>
                            </Stack>
                            <Stack
                                direction={"row"}
                                gap={"calc(var(--flex-gap)/4)"}
                            >
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                >
                                    Preffered Contact Method:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={500}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                    whiteSpace={"normal"}
                                >
                                    {customer?.email && customer?.phone ? "Email and Phone" : customer?.email ? "Email" : customer?.phone ? "Phone" : "Not Available"}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                    <Card
                        sx={{
                            borderRadius: "1rem", border: "1px solid var(--border-diluted-violet-color)", flex: 1
                        }}
                    >
                        <CardContent
                            className="application-information card-content"
                        >
                            <Typography
                                variant="h3"
                                fontFamily={"Poppins"}
                                fontWeight={700}
                                fontSize={20}
                                lineHeight={"normal"}
                                color="var(--dark-color)"
                                marginBlockEnd={"var(--basic-margin)"}
                            >
                                Application Details
                            </Typography>
                            <Stack
                                direction={"row"}
                                gap={"calc(var(--flex-gap)/4)"}
                                marginBlockEnd={"calc(var(--basic-margin)/2)"}
                            >
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                >
                                    Application Last Modified Date:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily={"Poppins"}
                                    fontWeight={500}
                                    fontSize={16}
                                    lineHeight={"normal"}
                                    color="var(--dark-color)"
                                    whiteSpace={"normal"}
                                >
                                    {new Date(customer?.updatedAt).toLocaleDateString("en-GB") || "Not Available"}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
                <Card
                    sx={{
                        borderRadius: "1rem", border: "1px solid var(--border-diluted-violet-color)", flex: 1
                    }}
                >
                    <CardContent
                        className="supporting-documents card-content"
                    >
                        <Typography
                            variant="h3"
                            fontFamily={"Poppins"}
                            fontWeight={700}
                            fontSize={20}
                            lineHeight={"normal"}
                            color="var(--dark-color)"
                            marginBlockEnd={"var(--basic-margin)"}
                        >
                            Supporting Document
                        </Typography>
                        <Stack
                            direction={{ desktop: "row" }}
                            gap={{ mobile: "calc(var(--flex-gap)/2)", tablet: "var(--flex-gap)" }}
                            alignItems={{ desktop: "center" }}
                            justifyContent={"space-between"}
                        >
                            <Stack
                                direction={{ mobile: "row" }}
                                gap={"calc(var(--flex-gap)/2)"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                bgcolor={"var(--diluted-primary-color)"}
                                padding={"calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"}
                                borderRadius={"16px"}
                            >
                                <Box
                                    flex={{ mobile: 1, desktop: 0.4 }}
                                    overflow={{ mobile: "hidden", desktop: "visible" }}
                                >
                                    <Typography
                                        variant="body1"
                                        fontFamily={"Poppins"}
                                        fontWeight={600}
                                        fontSize={20}
                                        lineHeight={"normal"}
                                        color="var(--dark-color)"
                                    >
                                        ID:
                                    </Typography>
                                </Box>
                                <Box
                                    flex={{ mobile: 1, desktop: 0.6 }}
                                    overflow={{ mobile: "hidden", desktop: "visible" }}
                                >
                                    <BaseButton
                                        variant="contained"
                                        radius="12px"
                                        padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                                        sx={{
                                            width: "100%"
                                        }}
                                        onClick={(e) => handleImageModalOpen(e, "validID")}
                                    >
                                        <Typography
                                            variant={"button"}
                                            fontFamily={"inherit"}
                                            fontWeight={"inherit"}
                                            fontSize={"inherit"}
                                            lineHeight={"inherit"}
                                            color={"inherit"}
                                            textTransform={"inherit"}
                                        >
                                            View
                                        </Typography>
                                    </BaseButton>
                                </Box>
                            </Stack>
                            <Stack
                                direction={{ mobile: "row" }}
                                gap={"calc(var(--flex-gap)/2)"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                bgcolor={"var(--diluted-primary-color)"}
                                padding={"calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"}
                                borderRadius={"16px"}
                            >
                                <Box
                                    flex={{ mobile: 1, desktop: 0.7 }}
                                    overflow={{ mobile: "hidden", desktop: "visible" }}
                                >
                                    <Typography
                                        variant="body1"
                                        fontFamily={"Poppins"}
                                        fontWeight={600}
                                        fontSize={20}
                                        lineHeight={"normal"}
                                        color="var(--dark-color)"
                                    >
                                        Passport Photo:
                                    </Typography>
                                </Box>
                                <Box
                                    flex={{ mobile: 1, desktop: 0.3 }}
                                    overflow={{ mobile: "hidden", desktop: "visible" }}
                                >
                                    <BaseButton
                                        variant="contained"
                                        radius="12px"
                                        padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                                        sx={{
                                            width: "100%"
                                        }}
                                        onClick={(e) => handleImageModalOpen(e, "passportPhoto")}
                                    >
                                        <Typography
                                            variant={"button"}
                                            fontFamily={"inherit"}
                                            fontWeight={"inherit"}
                                            fontSize={"inherit"}
                                            lineHeight={"inherit"}
                                            color={"inherit"}
                                            textTransform={"inherit"}
                                        >
                                            View
                                        </Typography>
                                    </BaseButton>
                                </Box>
                            </Stack>
                            <Stack
                                direction={{ mobile: "row" }}
                                gap={"calc(var(--flex-gap)/2)"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                bgcolor={"var(--diluted-primary-color)"}
                                padding={"calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"}
                                borderRadius={"16px"}
                            >
                                <Box
                                    flex={{ mobile: 1, desktop: 0.6 }}
                                    overflow={{ mobile: "hidden", desktop: "visible" }}
                                >
                                    <Typography
                                        variant="body1"
                                        fontFamily={"Poppins"}
                                        fontWeight={600}
                                        fontSize={20}
                                        lineHeight={"normal"}
                                        color="var(--dark-color)"
                                    >
                                        Signature:
                                    </Typography>
                                </Box>
                                <Box
                                    flex={{ mobile: 1, desktop: 0.4 }}
                                    overflow={{ mobile: "hidden", desktop: "visible" }}
                                >
                                    <BaseButton
                                        variant="contained"
                                        radius="12px"
                                        padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                                        sx={{
                                            width: "100%"
                                        }}
                                        onClick={(e) => handleImageModalOpen(e, "signature")}
                                    >
                                        <Typography
                                            variant={"button"}
                                            fontFamily={"inherit"}
                                            fontWeight={"inherit"}
                                            fontSize={"inherit"}
                                            lineHeight={"inherit"}
                                            color={"inherit"}
                                            textTransform={"inherit"}
                                        >
                                            View
                                        </Typography>
                                    </BaseButton>
                                </Box>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
                {error && <Typography
                    fontFamily={"Inter"}
                    fontWeight={"600"}
                    fontSize={14}
                    lineHeight={"normal"}
                    color={"var(--text-salmon-pink-color)"}
                    whiteSpace={"normal"}
                >
                    {error}
                </Typography>}
                <Stack
                    direction={{ miniTablet: "row" }}
                    gap={"calc(var(--flex-gap)/2)"}
                    alignItems={{ miniTablet: "center" }}
                >
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                            onClick={(e) => handleCustomerApprovalRequestAction(e, "approve")}
                        >
                            {isApproveLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Approve
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            variant="contained"
                            bgcolor="var(--disabled-grey-color)"
                            colour="var(--dark-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                            onClick={(e) => handleCustomerApprovalRequestAction(e, "decline")}
                        >
                            {isDeclineLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Decline
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </Stack>
            </CustomerApprovalActionWrapper>
        </AppLayout>
    )
}